import type { CSSProperties, ReactNode } from 'react'
import { CloseOutlined } from '@ant-design/icons'

/** 抽屉 header 右侧关闭：竖线 + X，对齐 Figma node 10051:174365 */
export function DrawerCloseExtra({ onClose }: { onClose: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        margin: '-4px 0',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 1,
          height: 30,
          flexShrink: 0,
          background: 'rgba(0, 0, 0, 0.04)',
        }}
      />
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          padding: 3,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: '#000',
          lineHeight: 1,
        }}
      >
        <CloseOutlined style={{ fontSize: 14 }} />
      </button>
    </div>
  )
}

/** 抽屉内区块标题（居中细线分隔，对齐 Figma Main Package / Platform Builds） */
export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        height: 28,
        color: '#000',
        fontSize: 15,
        fontWeight: 500,
        lineHeight: '28px',
      }}
    >
      <span
        style={{
          flex: 1,
          height: 1,
          background: 'rgba(0,0,0,0.06)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        }}
      />
      <span>{children}</span>
      <span
        style={{
          flex: 1,
          height: 1,
          background: 'rgba(0,0,0,0.06)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        }}
      />
    </div>
  )
}

/** 白卡内标题：左侧 indigo 竖条 + 16px Medium（对齐 Figma App Fixed / Display / …） */
export function CardTitle({
  children,
  required,
}: {
  children: ReactNode
  required?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <span
        style={{
          width: 2,
          height: 14,
          flexShrink: 0,
          background: 'var(--color-brand-indigo)',
          borderRadius: 1,
        }}
      />
      <span style={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: '#000' }}>
        {children}
        {required && (
          <span style={{ color: '#E53E3E', marginLeft: 4, fontWeight: 400 }}>*</span>
        )}
      </span>
    </div>
  )
}

/** 左标题 + 内容的表单行 */
export function Field({
  label,
  required,
  hint,
  children,
  style,
}: {
  label: ReactNode
  required?: boolean
  hint?: ReactNode
  children: ReactNode
  style?: CSSProperties
}) {
  return (
    <div style={{ marginBottom: 16, ...style }}>
      <div
        style={{
          fontSize: 13,
          color: 'rgba(0,0,0,0.85)',
          marginBottom: 6,
        }}
      >
        {label}
        {required && <span style={{ color: '#F53F3F', marginLeft: 2 }}>*</span>}
      </div>
      {children}
      {hint && (
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.4)', marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  )
}

/** 配置区块白卡：抽屉主体为磨砂灰底，每个配置项是一张白色卡片（对齐 Figma 10051:174363） */
export function SectionCard({
  children,
  style,
}: {
  children: ReactNode
  style?: React.CSSProperties
}) {
  return (
    <div
      style={{
        border: '1px solid var(--color-border-line)',
        borderRadius: 20,
        padding: 20,
        background: 'var(--color-background-module-container)',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** 卡片式容器 */
export function Panel({
  children,
  style,
}: {
  children: ReactNode
  style?: React.CSSProperties
}) {
  return (
    <div
      style={{
        border: '1px solid var(--color-border-card)',
        borderRadius: 12,
        padding: 16,
        background: '#fff',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** 上传区次要操作（重新上传 / 重新选择文件） */
export function ActionButton({
  children,
  icon,
  onClick,
}: {
  children: ReactNode
  icon?: ReactNode
  onClick?: () => void
}) {
  return (
    <button type="button" onClick={onClick} style={actionBtnStyle}>
      {children}
      {icon}
    </button>
  )
}

const actionBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  height: 32,
  padding: '6px 18px',
  borderRadius: 6,
  border: '1px solid var(--color-border-line)',
  background: 'var(--color-background-module-container)',
  backdropFilter: 'blur(6px)',
  cursor: 'pointer',
  fontSize: 13,
  lineHeight: '20px',
  color: '#000',
  flexShrink: 0,
}
